import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Mail, Loader, Eye, EyeOff } from 'lucide-react';
import AuthLayout from '@/components/auth/AuthLayout';
import { requestPasswordOTP, resetPassword } from '@/service/authService';

const EMAIL_REGEX = /^\S+@\S+\.\S+$/;
const COOLDOWN_SECONDS = 180; // 3 minutes

const ResetPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [stage, setStage] = useState<'email' | 'verify'>('email');

  const [otpLoading, setOtpLoading] = useState(false);
  const [continueLoading, setContinueLoading] = useState(false);


  const [otp, setOtp] = useState<string[]>(Array(6).fill(''));
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [cooldown, setCooldown] = useState(0);

  const navigate = useNavigate();


  // Kick off the cooldown timer when it’s set
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  // Send or Resend OTP
  const sendOtp = async () => {
    if (!EMAIL_REGEX.test(email)) {
      return toast.error('Invalid email format');
    }
    setOtpLoading(true);
    const { data, error } = await requestPasswordOTP(email);
    setOtpLoading(false);

    if (error) {
      return toast.error(error.message || 'Could not send OTP. Try again.');
    }
    if (!data?.success) {
      return toast.error(data?.message || 'Email not available');
    }

    toast.success('OTP sent. Check your email.');
    setStage('verify');
    setCooldown(COOLDOWN_SECONDS);
    setTimeout(() => inputsRef.current[0]?.focus(), 100);
  };

  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (stage === 'email') return sendOtp();

    const otpStr = otp.join('');
    if (otp.some(d => !d)) return toast.error('Enter full OTP');
    if (!password || !confirmPassword) return toast.error('Enter both passwords');
    if (password !== confirmPassword) return toast.error('Passwords do not match');

    setContinueLoading(true);
    const { data, error } = await resetPassword(email, password, otpStr);
    setContinueLoading(false);

    if (error) {
      return toast.error(error.message || 'Failed to reset password');
    }
    if (!data?.success) {
      return toast.error(data?.message || 'Password reset failed');
    }

    toast.success('Password reset successfully');
    setTimeout(() => navigate('/customer-login'), 1500);
  };

  const handleInputChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);
    if (value && index < 5) inputsRef.current[index + 1]?.focus();
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text').trim();
    if (!/^\d{6}$/.test(text)) return toast.error('Paste 6 digits only');
    const arr = text.split('');
    setOtp(arr);
    arr.forEach((val, i) => {
      if (inputsRef.current[i]) inputsRef.current[i]!.value = val;
    });
    inputsRef.current[5]?.focus();
  };

  // Decide button label & disabled state
  const otpButtonLabel = () => {
    if (otpLoading && stage === 'email') return <Loader className="tw-h-5 tw-w-5 tw-animate-spin" />;
    if (stage === 'email') return 'Send OTP';
    // verify stage
    if (cooldown > 0) return `Resend OTP (${formatTime(cooldown)})`;
    return 'Resend OTP';
  };
  const otpButtonDisabled = otpLoading || (stage === 'verify' && cooldown > 0);

  // mask util — keeps first 2 chars, masks the rest of local part
  const maskEmail = (raw?: string) => {
    if (!raw) return '';
    const parts = raw.split('@');
    if (parts.length < 2) return raw;
    const [local, domain] = parts;
    const visible = local.slice(0, 2);
    const starsCount = Math.max(6, local.length - 2); // ensure reasonable mask length
    const stars = '*'.repeat(starsCount);
    return `${visible}${stars}@${domain}`;
  };


  return (
    <AuthLayout
      title="Forgot Password"
      subtitle={
        stage === 'email'
          ? 'Enter your email to receive a 4-digit verification code. We will send the code to that email.'
          : `A 4-digit verification code has been sent to ${maskEmail(email)}. Enter the code below and set your new password.`
      }
    >
      <form className="" onSubmit={handleContinue}>

        {/* EMAIL FIELD + BUTTON */}
        <div className={` tw-flex-col tw-gap-4 ${stage === 'email' ? 'tw-flex' : 'tw-hidden'}`}>
          <div className="tw-flex-1 tw-relative">
            <div className="tw-absolute tw-inset-y-0 tw-left-0 tw-pl-3 tw-flex tw-items-center">
              <Mail className="tw-h-5 tw-w-5 tw-text-gray-500" />
            </div>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Email"
              disabled={stage === "verify"}
              className="tw-py-4 tw-pl-10 tw-block focus:tw-border-b-4 tw-w-full tw-bg-transparent tw-border tw-rounded-md focus:tw-outline-none focus:tw-border-b-[#009F40] border-b tw-text-gray-200 tw-text-sm"
              required
            />
          </div>
          <button
            type="button"
            onClick={sendOtp}
            disabled={otpButtonDisabled}
            className={`tw-flex tw-items-center tw-z-10 tw-justify-center tw-p-3 tw-rounded-md tw-text-sm tw-font-medium
    ${otpButtonDisabled
                ? 'tw-bg-cyan-600 tw-cursor-not-allowed tw-opacity-70'
                : 'tw-bg-[#00C5FB] hover:tw-bg-cyan-300 tw-transition-colors tw-duration-300'} 
    tw-text-black`}
          >
            {otpButtonLabel()}
          </button>

        </div>

        {/* OTP & PASSWORD FIELDS */}
        {stage === 'verify' && (
          <>
            <div className=' tw-flex tw-flex-col tw-gap-5'>

              <div onPaste={handlePaste}>
                <div className="tw-flex tw-justify-between tw-mb-10 tw-max-w-xs tw-mx-auto">
                  {otp.map((d, i) => (
                    <input
                      key={i}
                      ref={el => inputsRef.current[i] = el}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      defaultValue={d}
                      onChange={e => handleInputChange(i, e.target.value)}
                      onKeyDown={e => handleKeyDown(i, e)}
                      className="tw-w-10 tw-h-10 tw-z-20 tw-bg-gray-900 tw-border tw-border-gray-700 tw-rounded-md tw-text-center tw-text-white tw-text-lg focus:tw-border-cyan-500"
                    />
                  ))}
                </div>
              </div>

              {/* PASSWORD */}
              <div className="tw-relative">
                <input
                  type={showPwd ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="New password"
                  className="tw-w-full tw-py-3 tw-pl-4 tw-pr-10 tw-bg-transparent tw-border tw-border-gray-700 tw-rounded-md focus:tw-border-cyan-500 tw-text-gray-200 tw-text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(p => !p)}
                  className="tw-absolute tw-inset-y-0  tw-right-0 tw-pr-3 tw-flex tw-items-center tw-text-gray-500 hover:tw-text-cyan-500 tw-transition-colors tw-duration-700 focus:tw-outline-none"
                >
                  {showPwd
                    ? <EyeOff className="tw-h-5 tw-w-5" />
                    : <Eye className="tw-h-5 tw-w-5" />}
                </button>
              </div>

              {/* CONFIRM PASSWORD */}
              <div className="tw-relative">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Confirm password"
                  className="tw-w-full tw-py-3 tw-pl-4 tw-pr-10 tw-bg-transparent tw-border tw-border-gray-700 tw-rounded-md focus:tw-border-cyan-500 tw-text-gray-200 tw-text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(c => !c)}
                  className="tw-absolute tw-inset-y-0 tw-right-0 tw-z-10 tw-pr-3 tw-flex tw-items-center tw-text-gray-500 hover:tw-text-cyan-500 tw-transition-colors tw-duration-700 focus:tw-outline-none"
                >
                  {showConfirm
                    ? <EyeOff className="tw-h-5 tw-w-5" />
                    : <Eye className="tw-h-5 tw-w-5" />}
                </button>
              </div>

              {/* CONTINUE BUTTON */}
              <button
                type="submit"
                disabled={continueLoading}
                className="tw-w-full tw-flex  tw-justify-center tw-z-10 tw-py-2 tw-px-4 tw-border tw-border-transparent tw-rounded-md tw-shadow-md tw-text-sm tw-font-medium tw-text-black tw-bg-[#00C5FB] hover:tw-bg-white focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 tw-transition-colors tw-duration-700 disabled:tw-opacity-70 disabled:tw-cursor-not-allowed"
              >
                {continueLoading
                  ? <Loader className="tw-h-5 tw-w-5 tw-animate-spin" />
                  : 'Continue'}
              </button>
            </div>
          </>
        )}
      </form>
    </AuthLayout >
  );
};

export default ResetPasswordPage;
